// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * DiffOverlay - Renders visual diff markers over the KiCanvas viewer
 * 
 * Shows added elements in green, removed elements in red, and modified elements
 * with a yellow/orange indicator. Uses the same coordinate transformation system
 * as comment markers to ensure accurate positioning during pan/zoom.
 * 
 * Props:
 *   diffData: The diff response from the API containing added/removed/modified arrays
 *   kicadToScreen: Function to convert KiCad coordinates to screen pixels
 *   containerSize: {width, height} of the viewport
 *   isVisible: Whether the overlay should be shown
 */
function DiffOverlay({ diffData, kicadToScreen, containerSize, isVisible }) {
  if (!isVisible || !diffData || !diffData.diff) {
    return null;
  }

  const { added, removed, modified } = diffData.diff;

  // Helper to check if a screen position is within visible bounds
  const isPositionVisible = (screenPos) => {
    if (!screenPos) return false;
    return screenPos.x >= -100 && screenPos.x <= containerSize.width + 100 &&
           screenPos.y >= -100 && screenPos.y <= containerSize.height + 100;
  };

  // Collect all wire/track elements for SVG rendering
  const wireElements = [];
  const movementArrows = [];
  
  // Collect component, label, sheet, via elements for div rendering
  const markerElements = [];

  // Process added elements (green)
  added?.forEach((element, index) => {
    const screenPos = getElementScreenPosition(element, kicadToScreen);
    if (!isPositionVisible(screenPos)) return;

    const elementWithScreen = {
      ...element,
      screenPos,
      diffType: 'added',
      key: `added-${element.category}-${index}`
    };

    if (element.category === 'wire' || element.category === 'track') {
      wireElements.push(elementWithScreen);
    } else {
      markerElements.push(elementWithScreen);
    }
  });

  // Process removed elements (red)
  removed?.forEach((element, index) => {
    const screenPos = getElementScreenPosition(element, kicadToScreen);
    if (!isPositionVisible(screenPos)) return;

    const elementWithScreen = {
      ...element,
      screenPos,
      diffType: 'removed',
      key: `removed-${element.category}-${index}`
    };

    if (element.category === 'wire' || element.category === 'track') {
      wireElements.push(elementWithScreen);
    } else {
      markerElements.push(elementWithScreen);
    }
  });

  // Process modified elements (yellow/amber)
  modified?.forEach((element, index) => {
    const screenPos = getElementScreenPosition(element, kicadToScreen);
    if (!isPositionVisible(screenPos)) return;

    // Get old position for movement visualization
    let oldScreenPos = null;
    if (element.old_x !== undefined && element.old_y !== undefined) {
      oldScreenPos = kicadToScreen(element.old_x, element.old_y);
    }

    const elementWithScreen = {
      ...element,
      screenPos,
      oldScreenPos,
      diffType: 'modified',
      key: `modified-${element.category}-${index}`
    };

    if (element.category === 'wire' || element.category === 'track') {
      wireElements.push(elementWithScreen);
    } else {
      markerElements.push(elementWithScreen);
    }

    // Add movement arrow if element was moved significantly
    if (oldScreenPos && screenPos) {
      const dx = screenPos.x - oldScreenPos.x;
      const dy = screenPos.y - oldScreenPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 5) {
        movementArrows.push({
          key: `arrow-${element.category}-${index}`,
          oldScreenPos,
          screenPos
        });
      }
    }
  });

  return (
    <div className="diff-overlay absolute inset-0 pointer-events-none z-15">
      {/* SVG layer for wires/tracks and movement arrows */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          {/* Glow filters for visibility */}
          <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Render wire/track changes */}
        {wireElements.map((element) => (
          <WireDiffMarker 
            key={element.key}
            element={element}
            kicadToScreen={kicadToScreen}
          />
        ))}

        {/* Render movement arrows for modified elements */}
        {movementArrows.map((arrow) => (
          <g key={arrow.key}>
            {/* Dotted line from old to new position */}
            <line
              x1={arrow.oldScreenPos.x}
              y1={arrow.oldScreenPos.y}
              x2={arrow.screenPos.x}
              y2={arrow.screenPos.y}
              stroke="#f59e0b"
              strokeWidth="2"
              strokeDasharray="4 4"
              opacity="0.7"
            />
            {/* Arrow head */}
            <circle
              cx={arrow.screenPos.x}
              cy={arrow.screenPos.y}
              r="4"
              fill="#f59e0b"
            />
          </g>
        ))}
      </svg>

      {/* Render component/symbol, label, sheet, and via markers */}
      {markerElements.map((element) => {
        if (element.category === 'symbol' || element.category === 'footprint') {
          return (
            <ComponentDiffMarker 
              key={element.key}
              element={element}
            />
          );
        }
        if (element.category === 'label') {
          return (
            <LabelDiffMarker
              key={element.key}
              element={element}
            />
          );
        }
        if (element.category === 'sheet') {
          return (
            <SheetDiffMarker
              key={element.key}
              element={element}
              kicadToScreen={kicadToScreen}
            />
          );
        }
        if (element.category === 'via' || element.category === 'junction') {
          return (
            <ViaDiffMarker
              key={element.key}
              element={element}
            />
          );
        }
        return null;
      })}
    </div>
  );
}

/**
 * Get screen position for different element types
 */
function getElementScreenPosition(element, kicadToScreen) {
  // Most elements have x, y directly
  if (element.x !== undefined && element.y !== undefined) {
    return kicadToScreen(element.x, element.y);
  }
  
  // Wires/tracks have x1, y1, x2, y2 - use midpoint
  if (element.x1 !== undefined && element.y1 !== undefined) {
    const midX = (element.x1 + element.x2) / 2;
    const midY = (element.y1 + element.y2) / 2;
    return kicadToScreen(midX, midY);
  }
  
  return null;
}

/**
 * Marker for component/symbol changes - uses highlighting without icons for modified
 */
function ComponentDiffMarker({ element }) {
  const { screenPos, diffType, reference, value, description } = element;
  
  if (!screenPos) return null;
  
  const colors = {
    added: { bg: 'bg-green-500', border: 'border-green-400', text: 'text-green-100' },
    removed: { bg: 'bg-red-500', border: 'border-red-400', text: 'text-red-100' },
    modified: { bg: 'bg-amber-500', border: 'border-amber-400', text: 'text-amber-100' },
  };
  
  const color = colors[diffType];
  // Only show +/- icons for added/removed, no icon for modified
  const icon = diffType === 'added' ? '+' : diffType === 'removed' ? '−' : null;
  
  return (
    <div 
      className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-default group"
      style={{ left: screenPos.x, top: screenPos.y }}
      title={description}
    >
      {/* Main marker */}
      <div className={`
        ${icon ? 'w-8 h-8' : 'w-6 h-6'} rounded-lg ${color.bg} ${color.border} border-2
        flex items-center justify-center
        shadow-lg font-bold text-white text-lg
        transition-transform hover:scale-110
      `}>
        {icon}
      </div>
      
      {/* Reference label */}
      {reference && (
        <div className={`
          absolute top-full left-1/2 -translate-x-1/2 mt-1
          px-2 py-0.5 rounded text-xs font-mono whitespace-nowrap
          ${color.bg} ${color.text} shadow-md
        `}>
          {reference}
          {value && <span className="opacity-75 ml-1">({value})</span>}
        </div>
      )}
      
      {/* Expanded tooltip on hover */}
      <div className={`
        absolute bottom-full left-1/2 -translate-x-1/2 mb-2
        px-3 py-2 rounded-lg text-xs whitespace-nowrap
        bg-st-primary border-2 border-st-border shadow-lg
        opacity-0 group-hover:opacity-100 transition-opacity
        pointer-events-none z-50
      `}>
        <div className={`font-medium ${color.text}`}>
          {diffType === 'added' ? 'Added' : diffType === 'removed' ? 'Removed' : 'Modified'}
        </div>
        <div className="text-st-muted mt-1">{description}</div>
      </div>
    </div>
  );
}

/**
 * Marker for wire/track changes
 */
function WireDiffMarker({ element, kicadToScreen }) {
  const { diffType, x1, y1, x2, y2 } = element;
  
  if (x1 === undefined) return null;
  
  const start = kicadToScreen(x1, y1);
  const end = kicadToScreen(x2, y2);
  
  if (!start || !end) return null;
  
  const color = diffType === 'added' ? '#22c55e' : diffType === 'removed' ? '#ef4444' : '#f59e0b';
  const filter = diffType === 'added' ? 'url(#glow-green)' : diffType === 'removed' ? 'url(#glow-red)' : 'none';
  
  return (
    <line
      x1={start.x}
      y1={start.y}
      x2={end.x}
      y2={end.y}
      stroke={color}
      strokeWidth="4"
      strokeLinecap="round"
      opacity="0.8"
      filter={filter}
    />
  );
}

/**
 * Marker for label changes
 */
function LabelDiffMarker({ element }) {
  const { screenPos, diffType, text, type } = element;
  
  if (!screenPos) return null;
  
  const colors = {
    added: 'bg-green-500/90 border-green-400',
    removed: 'bg-red-500/90 border-red-400',
    modified: 'bg-amber-500/90 border-amber-400',
  };
  
  // Only show +/- icons for added/removed
  const icon = diffType === 'added' ? '+' : diffType === 'removed' ? '−' : null;
  
  return (
    <div 
      className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
      style={{ left: screenPos.x, top: screenPos.y }}
      title={`${diffType} ${type}: ${text}`}
    >
      <div className={`
        px-2 py-1 rounded border-2 ${colors[diffType]}
        flex items-center gap-1 shadow-lg
        text-xs font-medium text-white
      `}>
        {icon && <span className="font-bold">{icon}</span>}
        <span className="max-w-32 truncate">{text}</span>
      </div>
    </div>
  );
}

/**
 * Marker for hierarchical sheet changes - no tilde for modified
 */
function SheetDiffMarker({ element, kicadToScreen }) {
  const { x, y, width, height, diffType, name } = element;
  
  const topLeft = kicadToScreen(x, y);
  const bottomRight = kicadToScreen(x + width, y + height);
  
  if (!topLeft || !bottomRight) return null;
  
  const screenWidth = bottomRight.x - topLeft.x;
  const screenHeight = bottomRight.y - topLeft.y;
  
  const colors = {
    added: { border: 'border-green-500', bg: 'bg-green-500/10', text: 'text-green-400' },
    removed: { border: 'border-red-500', bg: 'bg-red-500/10', text: 'text-red-400' },
    modified: { border: 'border-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-400' },
  };
  
  const color = colors[diffType];
  // Only show +/- icons for added/removed
  const icon = diffType === 'added' ? '+ ' : diffType === 'removed' ? '− ' : '';
  
  return (
    <div
      className={`absolute ${color.border} border-2 ${color.bg} rounded pointer-events-auto`}
      style={{
        left: topLeft.x,
        top: topLeft.y,
        width: Math.abs(screenWidth),
        height: Math.abs(screenHeight),
      }}
      title={`${diffType} sheet: ${name}`}
    >
      <div className={`absolute -top-6 left-2 px-2 py-0.5 rounded text-xs font-medium ${color.text} bg-st-primary`}>
        {icon}{name}
      </div>
    </div>
  );
}

/**
 * Marker for via/junction changes
 */
function ViaDiffMarker({ element }) {
  const { screenPos, diffType, category } = element;
  
  if (!screenPos) return null;
  
  const colors = {
    added: '#22c55e',
    removed: '#ef4444',
    modified: '#f59e0b',
  };
  
  const size = category === 'via' ? 12 : 8;
  
  return (
    <div 
      className="absolute transform -translate-x-1/2 -translate-y-1/2"
      style={{ left: screenPos.x, top: screenPos.y }}
      title={`${diffType} ${category}`}
    >
      <div 
        className="rounded-full border-2"
        style={{
          width: size,
          height: size,
          backgroundColor: colors[diffType],
          borderColor: colors[diffType],
          opacity: 0.8,
        }}
      />
    </div>
  );
}

export default DiffOverlay;
